/**
 * 已放置物件渲染器 — 全面改進版
 *
 * - 松樹/闊葉：使用 EZTree 程序化 3D 幾何 + TSL 風吹動畫
 * - 岩石：青綠模式用 TSL 程序化著色（石青/赭石 + 墨線 + 宣紙紋理）
 * - 點苔（原灌木）：貼地墨點群落，模擬中國畫「點苔」技法
 */

import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import {
  Fn,
  vec2,
  vec3,
  vec4,
  float,
  positionLocal,
  positionWorld,
  normalLocal,
  normalWorld,
  uv,
  smoothstep,
  mix,
  clamp,
  normalize,
  pow,
  fract,
  sin,
  cos,
  dot,
  floor,
  abs,
  max,
  min,
  time as tslTime,
  texture,
  length,
} from 'three/tsl'
import { useGameStore } from '../core/store/gameStore'
import { terrainBuffer } from '../core/terrainBuffer'
import type { MapStyle, PlacedObject, PlacedObjectType } from '../core/store/gameStore'
import { generateTreeFromPreset } from '../lib/ez-tree'

// ── 千里江山圖礦物顏料色（提亮版：與石青石綠山體協調） ──
const QINGLV_COLORS = {
  trunk: '#4A3D35',      // 淡墨焦茶（暖棕而非純黑）
  trunkHanlin: '#3A3028',// 寒林枯幹：稍深墨色
  leafPine: '#4A7A60',   // 石綠偏深：與山體石綠同色系但略深
  leafBroad: '#5C9470',  // 三綠/銅綠：明亮粉綠
  leafWillow: '#72A050',  // 汁綠：明亮黃綠
  rock1: '#2A4A60',      // 石青
  rock2: '#6B5038',      // 赭石
  mossDot: '#2A4A38',    // 點苔：墨綠（匹配青綠山水色調）
}

const REALISTIC_COLORS = {
  rock: '#8A8478',
  mossDark: '#3A5530',
}

// ── 紋理載入（寫實模式用） ──
const textureLoader = new THREE.TextureLoader()

/** 延遲載入樹幹紋理 */
function loadBarkTextures(barkType: string) {
  const base = `./textures/tree/bark/${barkType}`
  const load = (suffix: string, srgb = true) => {
    const tex = textureLoader.load(`${base}_${suffix}_1k.jpg`)
    tex.wrapS = THREE.RepeatWrapping
    tex.wrapT = THREE.RepeatWrapping
    if (srgb) tex.colorSpace = THREE.SRGBColorSpace
    return tex
  }
  return {
    color: load('color'),
    ao: load('ao', false),
    normal: load('normal', false),
    roughness: load('roughness', false),
  }
}

/** 延遲載入樹葉紋理 */
function loadLeafTexture(leafType: string) {
  const tex = textureLoader.load(`./textures/tree/leaves/${leafType}_color.png`)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// ── 幾何體快取 ──
interface TreeCache {
  branchGeo: THREE.BufferGeometry
  leafGeo: THREE.BufferGeometry
}

const treeGeometryCache = new Map<string, TreeCache>()

import type { PresetName } from '../lib/ez-tree'

function getTreeGeo(presetName: PresetName, seed: number): TreeCache {
  const key = `${presetName}_${seed}`
  if (treeGeometryCache.has(key)) return treeGeometryCache.get(key)!
  const { branchGeometry, leafGeometry } = generateTreeFromPreset(presetName, seed)
  const cache = { branchGeo: branchGeometry, leafGeo: leafGeometry }
  treeGeometryCache.set(key, cache)
  return cache
}

// ── TSL 噪聲函數 ──
const tslHash21 = Fn(([p_immutable]: [any]) => {
  const p = vec2(p_immutable).toVar()
  return fract(sin(dot(p, vec2(127.1, 311.7))).mul(float(43758.5453)))
})

const tslValueNoise = Fn(([p_immutable]: [any]) => {
  const p = vec2(p_immutable).toVar()
  const i = floor(p)
  const f = fract(p)
  const u = f.mul(f).mul(float(3).sub(f.mul(float(2))))
  const a = tslHash21(i)
  const b = tslHash21(i.add(vec2(1, 0)))
  const c = tslHash21(i.add(vec2(0, 1)))
  const d = tslHash21(i.add(vec2(1, 1)))
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y)
})

// ── EZTree 搭配風吹動畫的材質 ──

/** 建立樹幹材質 */
function createBranchMaterial(style: MapStyle, barkType: string): THREE.MeshStandardNodeMaterial {
  const mat = new THREE.MeshStandardNodeMaterial()

  // 兩種模式都共用載入紋理
  const textures = loadBarkTextures(barkType)
  mat.map = textures.color
  mat.aoMap = textures.ao
  mat.normalMap = textures.normal
  mat.roughnessMap = textures.roughness
  mat.roughness = 0.85

  if (style !== 'realistic') {
    const trunkHex = style === 'shuimo' ? '#3a342c' : QINGLV_COLORS.trunk
    const c = new THREE.Color(trunkHex)
    
    // 將 base color 設為全黑以屏敝環境光照
    mat.colorNode = vec4(0, 0, 0, 1)
    
    // 使用 emissive 來做無光照平塗，加入垂直漸變模擬沾墨厚度
    const yRatio = clamp(positionLocal.y.mul(float(0.2)), float(0.0), float(1.0))
    const darkInk = vec3(0.08, 0.07, 0.05)
    const baseColor = vec3(c.r, c.g, c.b).mul(float(0.8))
    
    mat.emissiveNode = mix(darkInk, baseColor, yRatio)
    mat.roughnessNode = float(1.0)
  }

  return mat
}

/** 建立樹葉材質（含 TSL 風吹動畫） */
function createLeafMaterial(style: MapStyle, leafType: string): THREE.MeshStandardNodeMaterial {
  const mat = new THREE.MeshStandardNodeMaterial()
  mat.side = THREE.DoubleSide

  // 兩種模式皆載入紋理，以保持葉片形狀 (結合 alphaTest 與 alphaToCoverage)
  const tex = loadLeafTexture(leafType)
  mat.map = tex
  // 修正 LOD 消失問題：大幅降低 alphaTest 閾值並啟用 alphaToCoverage
  mat.alphaTest = 0.05
  mat.alphaToCoverage = true
  mat.roughness = 0.7

  if (style !== 'realistic') {
    let hex = QINGLV_COLORS.leafPine
    if (leafType === 'oak' || leafType === 'broad_dot') hex = QINGLV_COLORS.leafBroad
    else if (leafType === 'ash') hex = QINGLV_COLORS.leafWillow
    if (style === 'shuimo') hex = '#2e2a26'
    
    const c = new THREE.Color(hex)
    const qColor = vec3(c.r, c.g, c.b)

    // ── 每片葉子僅做微弱明度變化（不改色相） ──
    const leafCell = floor(positionLocal.xyz.mul(float(0.6)))
    const leafHash = fract(sin(
      dot(leafCell.xy.add(leafCell.z.mul(float(31.17))), vec2(127.1, 311.7))
    ).mul(float(43758.5453)))
    // 明度微調：0.90×~1.08×，足以區分葉片但不會顯花
    const leafBright = mix(float(0.90), float(1.08), leafHash)
    const leafColor = qColor.mul(leafBright)

    // ── 利用紋理的原始明暗資訊增加有機質感 ──
    const texColor = texture(tex)
    const texLum = texColor.r.mul(float(0.3)).add(texColor.g.mul(float(0.5))).add(texColor.b.mul(float(0.2)))
    const texInfluence = mix(float(0.85), float(1.10), texLum)

    // ── 體積感：內深外淺（積墨效果） ──
    const distFromCenter = length(positionLocal.xyz)
    const depthFactor = smoothstep(float(0.5), float(5.0), distFromCenter)
    const depthBright = mix(float(0.78), float(1.08), depthFactor)

    // ── 高度漸變（上淺下深，幅度小） ──
    const yRatio = clamp(positionLocal.y.sub(float(1.0)).mul(float(0.12)), float(0.0), float(1.0))
    const yBright = mix(float(0.88), float(1.05), yRatio)

    // ── 組合 ──
    const finalLeafColor = clamp(leafColor.mul(texInfluence).mul(depthBright).mul(yBright), float(0.0), float(1.0))

    // ── 墨線邊緣 ──
    const edgeFactor = float(1.0).sub(smoothstep(float(0.08), float(0.45), texColor.a))
    const inkDarken = mix(float(1.0), float(0.55), edgeFactor)

    // ── 法線微妙明暗 ──
    const nDotUp = clamp(normalLocal.y.mul(float(0.5)).add(float(0.5)), float(0.0), float(1.0))
    const normalBright = mix(float(0.88), float(1.06), nDotUp)

    const shadedLeaf = clamp(finalLeafColor.mul(inkDarken).mul(normalBright), float(0.0), float(1.0))

    mat.colorNode = vec4(0, 0, 0, texColor.a)
    mat.emissiveNode = shadedLeaf
    mat.roughnessNode = float(1.0)
  }

  // ── TSL 風吹頂點動畫 ──
  // 基於 UV.y（葉的上部搖擺更多）和世界位置 hash 做相位差
  mat.positionNode = Fn(() => {
    const pos = positionLocal.toVar()
    const uvCoord = uv()
    const time = tslTime

    // 用世界位置做相位偏移，讓不同位置的葉子不同步
    const worldPos = positionWorld
    const phaseOffset = sin(worldPos.x.mul(float(0.3)).add(worldPos.z.mul(float(0.7)))).mul(float(6.28))

    // 風搖擺幅度（UV.y=1 的頂端搖最多）
    const swayAmount = uvCoord.y.mul(float(0.8))

    // 多頻率疊加產生自然風感
    const windX = swayAmount.mul(
      sin(time.mul(float(1.5)).add(phaseOffset)).mul(float(0.5))
        .add(sin(time.mul(float(3.2)).add(phaseOffset.mul(float(1.3)))).mul(float(0.3)))
        .add(sin(time.mul(float(7.5)).add(phaseOffset.mul(float(1.7)))).mul(float(0.15)))
    )
    const windZ = swayAmount.mul(
      cos(time.mul(float(1.2)).add(phaseOffset.mul(float(0.8)))).mul(float(0.3))
        .add(cos(time.mul(float(4.0)).add(phaseOffset.mul(float(1.1)))).mul(float(0.1)))
    )

    return vec3(pos.x.add(windX), pos.y, pos.z.add(windZ))
  })()

  return mat
}

// ── 材質快取 ──
const materialCache = new Map<string, { branch: THREE.MeshStandardNodeMaterial; leaf: THREE.MeshStandardNodeMaterial }>()

function getTreeMaterials(style: MapStyle, barkType: string, leafType: string) {
  const key = `${style}_${barkType}_${leafType}`
  if (materialCache.has(key)) return materialCache.get(key)!
  const branch = createBranchMaterial(style, barkType)
  const leaf = createLeafMaterial(style, leafType)
  const mats = { branch, leaf }
  materialCache.set(key, mats)
  return mats
}

// ══════════════════════════════════════════
// 物件元件
// ══════════════════════════════════════════

interface ObjectProps {
  position: [number, number, number]
  rotation: number
  scale: number
}

/** 將 group 的 Y 座標吸附到地形高度，僅在高度圖版本變更時重算 */
function useGroundSnap(
  groupRef: React.RefObject<THREE.Group | null>,
  x: number, z: number,
  offset = 0,
) {
  const lastVer = useRef(-1)
  useFrame(() => {
    if (!groupRef.current || !terrainBuffer.ready) return
    if (terrainBuffer.getHeightVersion === lastVer.current) return
    lastVer.current = terrainBuffer.getHeightVersion
    groupRef.current.position.y = terrainBuffer.getHeight(x, z) + offset
  })
}

/** EZTree 松樹 */
function PineTree({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)

  const seed = useMemo(() => Math.floor(Math.abs(position[0] * 1000 + position[2] * 7)), [position])
  const { branchGeo, leafGeo } = useMemo(() => getTreeGeo('pine_medium', seed % 7), [seed])
  const { branch: branchMat, leaf: leafMat } = useMemo(
    () => getTreeMaterials(mapStyle, 'pine', 'pine'),
    [mapStyle],
  )

  useGroundSnap(groupRef, position[0], position[2])

  // 縮放：EZTree 預設尺寸較大，需要縮小到適合地形的比例
  const treeScale = scale * 0.16

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]} rotation={[0, rotation, 0]} scale={treeScale}>
      <mesh geometry={branchGeo} material={branchMat} castShadow receiveShadow />
      {leafGeo.attributes.position && (
        <mesh geometry={leafGeo} material={leafMat} castShadow receiveShadow />
      )}
    </group>
  )
}

/** EZTree 闊葉樹 */
function BroadTree({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)

  const seed = useMemo(() => Math.floor(Math.abs(position[0] * 1000 + position[2] * 7)), [position])
  const { branchGeo, leafGeo } = useMemo(() => getTreeGeo('oak_medium', seed % 7), [seed])
  const { branch: branchMat, leaf: leafMat } = useMemo(
    () => getTreeMaterials(mapStyle, 'oak', 'oak'),
    [mapStyle],
  )

  useGroundSnap(groupRef, position[0], position[2])

  const treeScale = scale * 0.16

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]} rotation={[0, rotation, 0]} scale={treeScale}>
      <mesh geometry={branchGeo} material={branchMat} castShadow receiveShadow />
      {leafGeo.attributes.position && (
        <mesh geometry={leafGeo} material={leafMat} castShadow receiveShadow />
      )}
    </group>
  )
}

/** EZTree 柳樹（真·垂柳：使用 willow_medium 下垂枝條 + ash 葉紋理） */
function WillowTree({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)

  const seed = useMemo(() => Math.floor(Math.abs(position[0] * 1000 + position[2] * 7)), [position])
  const { branchGeo, leafGeo } = useMemo(() => getTreeGeo('willow_medium', seed % 7), [seed])
  const { branch: branchMat, leaf: leafMat } = useMemo(
    () => getTreeMaterials(mapStyle, 'willow', 'ash'),
    [mapStyle],
  )

  useGroundSnap(groupRef, position[0], position[2])

  const treeScale = scale * 0.16

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]} rotation={[0, rotation, 0]} scale={treeScale}>
      <mesh geometry={branchGeo} material={branchMat} castShadow receiveShadow />
      {leafGeo.attributes.position && (
        <mesh geometry={leafGeo} material={leafMat} castShadow receiveShadow />
      )}
    </group>
  )
}

/** 寒林（枯枝疏影：只有樹幹/枝條，無葉） */
function HanlinTree({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)

  const seed = useMemo(() => Math.floor(Math.abs(position[0] * 1000 + position[2] * 7)), [position])
  // 寒林使用 crabclaw (蟹爪枝) 預設
  const { branchGeo } = useMemo(() => getTreeGeo('crabclaw', seed % 7), [seed])

  const branchMat = useMemo(() => {
    const mat = new THREE.MeshStandardNodeMaterial()
    const textures = loadBarkTextures('birch')
    mat.map = textures.color
    mat.aoMap = textures.ao
    mat.normalMap = textures.normal
    mat.roughnessMap = textures.roughness
    mat.roughness = 0.85

    if (mapStyle !== 'realistic') {
      const c = new THREE.Color(mapStyle === 'shuimo' ? '#2a2622' : QINGLV_COLORS.trunkHanlin)
      const qColor = vec3(c.r, c.g, c.b)
      const texColor = texture(textures.color)
      const lum = dot(texColor.rgb, vec3(0.299, 0.587, 0.114))
      const brightLum = max(lum, float(0.15)).mul(float(1.6))
      mat.colorNode = vec4(qColor.mul(brightLum), float(1))
      mat.roughnessNode = float(0.92)
    }

    return mat
  }, [mapStyle])

  useGroundSnap(groupRef, position[0], position[2])

  const treeScale = scale * 0.16

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]} rotation={[0, rotation, 0]} scale={treeScale}>
      <mesh geometry={branchGeo} material={branchMat} castShadow receiveShadow />
    </group>
  )
}

/** 白楊樹 */
function AspenTree({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)

  const seed = useMemo(() => Math.floor(Math.abs(position[0] * 1000 + position[2] * 7)), [position])
  const { branchGeo, leafGeo } = useMemo(() => getTreeGeo('aspen_medium', seed % 7), [seed])
  const { branch: branchMat, leaf: leafMat } = useMemo(
    () => getTreeMaterials(mapStyle, 'birch', 'oak'),
    [mapStyle],
  )

  useGroundSnap(groupRef, position[0], position[2])

  const treeScale = scale * 0.16

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]} rotation={[0, rotation, 0]} scale={treeScale}>
      <mesh geometry={branchGeo} material={branchMat} castShadow receiveShadow />
      {leafGeo.attributes.position && (
        <mesh geometry={leafGeo} material={leafMat} castShadow receiveShadow />
      )}
    </group>
  )
}

/** 白蠟樹 */
function AshTree({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)

  const seed = useMemo(() => Math.floor(Math.abs(position[0] * 1000 + position[2] * 7)), [position])
  const { branchGeo, leafGeo } = useMemo(() => getTreeGeo('ash_medium', seed % 7), [seed])
  const { branch: branchMat, leaf: leafMat } = useMemo(
    () => getTreeMaterials(mapStyle, 'oak', 'ash'),
    [mapStyle],
  )

  useGroundSnap(groupRef, position[0], position[2])

  const treeScale = scale * 0.16

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]} rotation={[0, rotation, 0]} scale={treeScale}>
      <mesh geometry={branchGeo} material={branchMat} castShadow receiveShadow />
      {leafGeo.attributes.position && (
        <mesh geometry={leafGeo} material={leafMat} castShadow receiveShadow />
      )}
    </group>
  )
}

/** 蟹爪枝 */
function CrabclawTree({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)

  const seed = useMemo(() => Math.floor(Math.abs(position[0] * 1000 + position[2] * 7)), [position])
  const { branchGeo } = useMemo(() => getTreeGeo('crabclaw', seed % 7), [seed])
  const { branch: branchMat } = useMemo(
    () => getTreeMaterials(mapStyle, 'birch', 'ash'),
    [mapStyle],
  )

  useGroundSnap(groupRef, position[0], position[2])

  const treeScale = scale * 0.16

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]} rotation={[0, rotation, 0]} scale={treeScale}>
      <mesh geometry={branchGeo} material={branchMat} castShadow receiveShadow />
    </group>
  )
}

/** 點葉群叢樹 */
function BroadDotTree({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)

  const seed = useMemo(() => Math.floor(Math.abs(position[0] * 1000 + position[2] * 7)), [position])
  const { branchGeo, leafGeo } = useMemo(() => getTreeGeo('broad_dot', seed % 7), [seed])
  const { branch: branchMat, leaf: leafMat } = useMemo(
    () => getTreeMaterials(mapStyle, 'oak', 'oak'),
    [mapStyle],
  )

  useGroundSnap(groupRef, position[0], position[2])

  const treeScale = scale * 0.16

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]} rotation={[0, rotation, 0]} scale={treeScale}>
      <mesh geometry={branchGeo} material={branchMat} castShadow receiveShadow />
      {leafGeo.attributes.position && (
        <mesh geometry={leafGeo} material={leafMat} castShadow receiveShadow />
      )}
    </group>
  )
}

/** EZTree 灌叢 */
function BushTree({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)

  const seed = useMemo(() => Math.floor(Math.abs(position[0] * 1000 + position[2] * 7)), [position])
  const { branchGeo, leafGeo } = useMemo(() => getTreeGeo('bush_1', seed % 7), [seed])
  const { branch: branchMat, leaf: leafMat } = useMemo(
    () => getTreeMaterials(mapStyle, 'oak', 'ash'),
    [mapStyle],
  )

  useGroundSnap(groupRef, position[0], position[2])

  const treeScale = scale * 0.16

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]} rotation={[0, rotation, 0]} scale={treeScale}>
      <mesh geometry={branchGeo} material={branchMat} castShadow receiveShadow />
      {leafGeo.attributes.position && (
        <mesh geometry={leafGeo} material={leafMat} castShadow receiveShadow />
      )}
    </group>
  )
}

// ── 岩石全局幾何快取 ──
const sharedRockGeo = (() => {
  const g1 = new THREE.IcosahedronGeometry(2.2, 1)
  const pos1 = g1.getAttribute('position')
  for (let i = 0; i < pos1.count; i++) {
    const x = pos1.getX(i)
    const y = pos1.getY(i)
    const z = pos1.getZ(i)
    const noise = 0.7 + 0.5 * Math.sin(i * 17.3 + x * 5.7)
    pos1.setXYZ(i, x * noise, y * 0.55 * noise, z * noise)
  }
  pos1.needsUpdate = true
  g1.computeVertexNormals()

  const g2 = new THREE.IcosahedronGeometry(1.5, 1)
  const pos2 = g2.getAttribute('position')
  for (let i = 0; i < pos2.count; i++) {
    const x = pos2.getX(i)
    const y = pos2.getY(i)
    const z = pos2.getZ(i)
    const noise = 0.65 + 0.45 * Math.sin(i * 23.1 + z * 3.3)
    pos2.setXYZ(i, x * noise, y * 0.45 * noise, z * noise)
  }
  pos2.needsUpdate = true
  g2.computeVertexNormals()

  return { geo1: g1, geo2: g2 }
})()

/** 岩石 — 青綠模式：TSL 程序化著色 / 寫實模式：灰褐 */
function Rock({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)
  const isPainted = mapStyle !== 'realistic'

  const { geo1, geo2 } = sharedRockGeo

  const qingLvMat = useMemo(() => {
    if (!isPainted) return null
    const mat = new THREE.MeshStandardNodeMaterial()
    mat.roughnessNode = float(0.92)
    mat.metalnessNode = float(0.0)

    mat.colorNode = Fn(() => {
      const wPos = positionWorld
      const nrm = normalWorld

      // 高度漸變：低處赭石、高處石青
      // 注意：使用 positionLocal.y 確保與外部高度解耦，有助於材質共用
      const heightFactor = smoothstep(float(0), float(4), positionLocal.y)

      // 底色
      const ochre = mapStyle === 'shuimo' ? vec3(0.55, 0.52, 0.46) : vec3(0.42, 0.31, 0.16)
      const azurite = mapStyle === 'shuimo' ? vec3(0.28, 0.26, 0.23) : vec3(0.17, 0.30, 0.42)
      const baseColor = mix(ochre, azurite, heightFactor)

      // 噪聲打破均勻感
      const noiseCoord = vec2(wPos.x.mul(float(0.5)), wPos.z.mul(float(0.5)))
      const brushNoise = tslValueNoise(noiseCoord.mul(float(2.0)))
      const colorVariation = brushNoise.sub(float(0.5)).mul(float(0.15))
      const variedColor = baseColor.add(vec3(colorVariation, colorVariation.mul(float(0.8)), colorVariation.mul(float(0.6))))

      // 墨線描邊 — 用法線方向偏導數模擬邊緣
      const edgeNdotUp = abs(dot(nrm, vec3(0, 1, 0)))
      const edgeFactor = smoothstep(float(0.3), float(0.1), edgeNdotUp)
      const inkColor = vec3(0.06, 0.04, 0.03)
      const withInk = mix(variedColor, inkColor, edgeFactor.mul(float(0.5)))

      // 皴法線條 — 沿垂直方向的條紋噪聲
      const cunNoise = tslValueNoise(vec2(wPos.y.mul(float(4.0)), wPos.x.add(wPos.z).mul(float(1.5))))
      const cunStrength = smoothstep(float(0.55), float(0.75), cunNoise)
      const cunColor = mix(withInk, withInk.mul(float(0.7)), cunStrength.mul(float(0.3)).mul(float(1).sub(edgeNdotUp)))

      // 宣紙紋理
      const paperNoise = tslValueNoise(noiseCoord.mul(float(8.0))).sub(float(0.5)).mul(float(0.04))
      const finalColor = cunColor.add(vec3(paperNoise))

      return vec4(finalColor, float(1))
    })()

    return mat
  }, [isPainted, mapStyle])

  const realisticMat = useMemo(() => {
    if (isPainted) return null
    const mat = new THREE.MeshStandardNodeMaterial()
    mat.colorNode = vec4(vec3(0.54, 0.52, 0.47), float(1))
    mat.roughnessNode = float(0.92)
    mat.metalnessNode = float(0.02)
    return mat
  }, [isPainted])

  const activeMat = isPainted ? qingLvMat : realisticMat

  useGroundSnap(groupRef, position[0], position[2])

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]}>
      <mesh
        geometry={geo1}
        material={activeMat!}
        rotation={[0, rotation, 0.15]}
        scale={[scale * 1.3, scale, scale]}
      />
      <mesh
        geometry={geo2}
        material={activeMat!}
        position={[1.2 * scale, 0.1, 0.6 * scale]}
        rotation={[0.1, rotation + 0.5, 0.1]}
        scale={[scale * 0.8, scale * 0.7, scale * 0.7]}
      />
    </group>
  )
}

// ── 點苔全局快取材質與幾何 ──
const sharedMossGeo = new THREE.SphereGeometry(1, 12, 8)
const sharedMossMatQingLv = new THREE.MeshStandardMaterial({ color: QINGLV_COLORS.mossDot, roughness: 0.95 })
const sharedMossMatShuimo = new THREE.MeshStandardMaterial({ color: '#2a2824', roughness: 0.95 })
const sharedMossMatRealistic = new THREE.MeshStandardMaterial({ color: REALISTIC_COLORS.mossDark, roughness: 0.85 })

// ── 雲霧補丁共享資源 ──
const sharedFogGeo = new THREE.PlaneGeometry(1, 1, 8, 8)
const sharedFogMat = (() => {
  const mat = new THREE.MeshBasicNodeMaterial()
  mat.transparent = true
  mat.side = THREE.DoubleSide
  mat.depthWrite = false
  mat.fog = false

  const time = tslTime

  // 噪聲函數（複用已有的 tslHash21 / tslValueNoise）
  const fogFbm = Fn(([p_immutable]: [any]) => {
    const p = vec2(p_immutable).toVar()
    const v = float(0).toVar()
    const a = float(0.5).toVar()
    const shift = vec2(100, 100)
    v.addAssign(a.mul(tslValueNoise(p))); p.assign(p.mul(2.03).add(shift)); a.mulAssign(0.5)
    v.addAssign(a.mul(tslValueNoise(p))); p.assign(p.mul(2.01).add(shift)); a.mulAssign(0.5)
    v.addAssign(a.mul(tslValueNoise(p))); p.assign(p.mul(1.97).add(shift)); a.mulAssign(0.5)
    v.addAssign(a.mul(tslValueNoise(p)))
    return v
  })

  // 頂點位移：輕微起伏（local Z → world Y，因旋轉 -PI/2）
  mat.positionNode = Fn(() => {
    const pos = positionLocal.toVar()
    const worldPos = positionWorld
    const displaceN = tslValueNoise(vec2(
      worldPos.x.div(15).add(time.mul(0.01)),
      worldPos.z.div(15).sub(time.mul(0.007)),
    ))
    pos.z.addAssign(displaceN.sub(0.5).mul(0.08))
    return pos
  })()

  // 片段：雲形 FBM × 圓形衰減
  mat.colorNode = Fn(() => {
    const fogColor = vec3(0.95, 0.92, 0.86)
    const uvCoord = uv()

    // 圓形漸消：中心濃、邊緣消散
    const distFromCenter = uvCoord.sub(vec2(0.5, 0.5)).length()
    const circularFade = float(1).sub(smoothstep(float(0.15), float(0.5), distFromCenter))

    // 雲形噪聲（基於世界座標流動）
    const worldPos = positionWorld
    const noiseUV = vec2(
      worldPos.x.div(25).add(time.mul(0.008)),
      worldPos.z.div(25).sub(time.mul(0.005)),
    )
    const cloud = fogFbm(noiseUV.mul(3))
    const cloudShape = smoothstep(float(0.28), float(0.58), cloud)

    const alpha = cloudShape.mul(circularFade).mul(0.35)
    return vec4(fogColor, alpha)
  })()

  return mat
})()

/** 可放置雲霧斑塊 — 飄浮在地形上方的局部霧氣 */
function FogPatch({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)

  useGroundSnap(groupRef, position[0], position[2], 5 + scale * 10)

  // scale 0.7~1.3 → patchSize 25~60
  const patchSize = 15 + scale * 35

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]} rotation={[0, rotation, 0]}>
      <mesh
        geometry={sharedFogGeo}
        material={sharedFogMat}
        rotation={[-Math.PI / 2, 0, 0]}
        scale={[patchSize, patchSize, 1]}
        renderOrder={800}
      />
    </group>
  )
}

/** 點苔 — 中國畫技法：圓潤半球嵌入地面，依坡面傾斜 */
function MossDot({ position, rotation, scale }: ObjectProps) {
  const groupRef = useRef<THREE.Group>(null)
  const mapStyle = useGameStore(s => s.mapStyle)

  const lastMossVer = useRef(-1)
  useFrame(() => {
    if (!groupRef.current || !terrainBuffer.ready) return
    if (terrainBuffer.getHeightVersion === lastMossVer.current) return
    lastMossVer.current = terrainBuffer.getHeightVersion

    const x = position[0], z = position[2]
    groupRef.current.position.y = terrainBuffer.getHeight(x, z)

    const step = 2.0
    const hL = terrainBuffer.getHeight(x - step, z)
    const hR = terrainBuffer.getHeight(x + step, z)
    const hD = terrainBuffer.getHeight(x, z - step)
    const hU = terrainBuffer.getHeight(x, z + step)
    const nx = (hL - hR) / (2 * step)
    const nz = (hD - hU) / (2 * step)
    groupRef.current.rotation.set(
      Math.atan(nz) * 0.9,
      rotation,
      Math.atan(-nx) * 0.9,
    )
  })

  const r = scale * 0.6
  const activeMat = mapStyle === 'shuimo'
    ? sharedMossMatShuimo
    : mapStyle === 'qinglv'
      ? sharedMossMatQingLv
      : sharedMossMatRealistic

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]}>
      {/* 球體下沉 60% 半徑，加扁平造型（Y 55%），陡坡上不懸空 */}
      <mesh geometry={sharedMossGeo} material={activeMat} position={[0, -r * 0.6, 0]} scale={[r, r * 0.55, r]} />
    </group>
  )
}

// ── 渲染器映射 ──

const RENDERERS: Record<PlacedObjectType, React.FC<ObjectProps>> = {
  tree_pine: PineTree,
  tree_broad: BroadTree,
  tree_willow: WillowTree,
  tree_hanlin: HanlinTree,
  tree_aspen: AspenTree,
  tree_ash: AshTree,
  tree_crabclaw: CrabclawTree,
  tree_broad_dot: BroadDotTree,
  tree_bush: BushTree,
  rock: Rock,
  bush: MossDot,
  cloud_fog: FogPatch,
}

export default function PlacedObjects() {
  const placedObjects = useGameStore(s => s.placedObjects)

  return (
    <>
      {placedObjects.map(obj => {
        const Renderer = RENDERERS[obj.type]
        return (
          <Renderer
            key={obj.id}
            position={obj.position}
            rotation={obj.rotation}
            scale={obj.scale}
          />
        )
      })}
    </>
  )
}
